import type { JSX } from "preact"
export type Element = JSX.Element

import { RecordAssign } from "@pdfking/tfunc"
import { batch, computed, effect, signal } from "@preact/signals"
import { h, render } from "preact"
import { useEffect, useRef } from "preact/hooks"

import type { NextFrame as NextFrameType } from "../public/base/Factory"
import { CreateReactivityFunctions } from "../public/base/Factory"
import { RunAllWith } from "../public/utils/Lifecycle"
import { DefineProperty } from "../public/utils/Properties"

const constructors = {
  effect,
  set_computed: (instance: any, key: string, get: () => any, set: (v: any) => void) => {
    const r = computed(get)
    DefineProperty(instance, key, () => r.value, set)
  },
  set_data: (instance: any, key: string, value: any) => {
    const r = signal(value)
    DefineProperty(
      instance,
      key,
      () => r.value,
      v => (r.value = v)
    )
  },
}

const mountPreact = (renderFn: () => JSX.Element, el: HTMLElement) => {
  render(h(renderFn, { el }), el)
  return el.firstChild!
}

// Component factory that persists instance using useRef
const preactComponentFactory =
  (
    init: (props: any) => any,
    config: {
      render: (children?: JSX.Element[]) => JSX.Element
      mounted?: any
      unmounted?: any
    }
  ) =>
  (props?: any) => {
    const children = props?.children
    const ref = useRef<any>(undefined)
    if (!ref.current) {
      ref.current = init(props)
    } else {
      // Each field is its own signal, and signal writes fire subscribed
      // effects synchronously — so assigning props one at a time lets a
      // watcher that reads a different prop (a cross-field invariant, e.g.
      // "does this new value match the marker in that other prop") observe a
      // torn mix of this render's and last render's values, depending on
      // where in `props` the two fields happen to fall. `batch` defers every
      // effect until all of this render's writes have landed, so any watcher
      // that fires sees this render's props in full, never a partial set.
      batch(() => RecordAssign(ref.current, props))
    }
    const vnode = config.render.call(ref.current, children)
    useEffect(() => {
      const el = (vnode as any)?.__e as HTMLElement | undefined
      RunAllWith(config.mounted, ref.current, el)
      return () => RunAllWith(config.unmounted, ref.current, el)
    }, [])
    return vnode
  }

export { CreateMixin } from "../public/base/Factory"
export const NextFrame: NextFrameType = fn => requestAnimationFrame(fn)
export const { Init, CreateRenderFunction, Mount } = CreateReactivityFunctions<JSX.Element>(
  constructors,
  mountPreact,
  preactComponentFactory,
  () => h("template", null)
)

export { signal } from "@preact/signals"
// Re-export h and signal for use in other packages
export { h } from "preact"
